// =============================================================================
// PTEL - usePermissions Hook
// FB.10.13 · Permisos derivados del rol del usuario
// =============================================================================

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types/roles';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface Permissions {
  /** Puede exportar XLSX / GeoJSON / CSV (editor+) */
  canExport: boolean;
  /** Puede editar coordenadas manualmente (editor+) */
  canEditCoords: boolean;
  /** Puede validar / aprobar resultados (editor+) */
  canValidate: boolean;
  /** Puede solicitar rol Editor (solo viewer autenticado) */
  canRequestEditor: boolean;
  /** Puede acceder al panel de administración (solo admin) */
  canAdmin: boolean;
  /** Puede acceder al panel de moderación (moderator + admin) */
  canModerate: boolean;
  /** Etiqueta legible del rol actual */
  roleLabel: string;
  /** Rol actual del usuario (null si no autenticado) */
  role: UserRole | null;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const ROLE_LABELS: Record<UserRole, string> = {
  admin:     'Administrador',
  moderator: 'Moderador',
  editor:    'Editor',
  viewer:    'Visor',
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Deriva permisos booleanos a partir del rol del usuario autenticado.
 *
 * - **editor+** = editor o admin
 * - **canRequestEditor** = solo viewer autenticado
 * - **canAdmin** = solo admin
 * - Sin autenticar → todo false, roleLabel = 'Sin autenticar'
 */
export function usePermissions(): Permissions {
  const { userData } = useAuth();

  return useMemo(() => {
    const role = userData?.role ?? null;
    const isEditorPlus = role === 'editor' || role === 'admin';

    return {
      canExport: isEditorPlus,
      canEditCoords: isEditorPlus,
      canValidate: isEditorPlus,
      canRequestEditor: role === 'viewer',
      canAdmin: role === 'admin',
      canModerate: role === 'moderator' || role === 'admin',
      roleLabel: role ? ROLE_LABELS[role] : 'Sin autenticar',
      role,
    };
  }, [userData?.role]);
}
